import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Refresh } from '@mui/icons-material';
import { getChampions, setChampionMargin, updateChampionMargin, deleteChampionMargin } from './referralUtils';

const ChampionsManagement = () => {
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChampion, setEditingChampion] = useState(null);
  const [marginInput, setMarginInput] = useState('');
  const [marginError, setMarginError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [championToDelete, setChampionToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadChampions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getChampions();
      setChampions(data.champions || []);
    } catch (err) {
      setError('Failed to load champions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChampions();
  }, []);

  const handleOpenDialog = (champion = null) => {
    setEditingChampion(champion);
    setMarginInput(champion?.margin_entitlement != null ? String(champion.margin_entitlement) : '');
    setMarginError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingChampion(null);
    setMarginInput('');
    setMarginError('');
  };

  const validateMargin = () => {
    const v = parseFloat(marginInput);
    if (marginInput === '' || isNaN(v)) {
      setMarginError('Enter a number between 0 and 100');
      return false;
    }
    if (v < 0 || v > 100) {
      setMarginError('Margin must be between 0 and 100');
      return false;
    }
    setMarginError('');
    return true;
  };

  const handleSaveMargin = async () => {
    if (!validateMargin() || !editingChampion) return;
    const value = parseFloat(marginInput);
    try {
      if (editingChampion.margin_entitlement != null) {
        await updateChampionMargin(editingChampion.id, value);
      } else {
        await setChampionMargin(editingChampion.id, value);
      }
      setSnackbar({ open: true, message: 'Margin saved', severity: 'success' });
      handleCloseDialog();
      loadChampions();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save', severity: 'error' });
    }
  };

  const handleDeleteClick = (champion) => {
    setChampionToDelete(champion);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!championToDelete) return;
    try {
      await deleteChampionMargin(championToDelete.id);
      setSnackbar({ open: true, message: 'Margin removed', severity: 'success' });
      setDeleteConfirmOpen(false);
      setChampionToDelete(null);
      loadChampions();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove margin', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Button startIcon={<Refresh />} onClick={loadChampions} variant="outlined">
          Refresh
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={loadChampions}>Retry</Button>}>
          {error}
        </Alert>
      )}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Champions ({champions.length})
          </Typography>
          {champions.length === 0 ? (
            <Alert severity="info">No champions found.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Margin Entitlement (%)</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {champions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.name || '—'}</TableCell>
                      <TableCell>{c.margin_entitlement != null ? `${c.margin_entitlement}%` : '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(c)} title="Set / Edit margin" color="primary">
                          <Edit />
                        </IconButton>
                        {c.margin_entitlement != null && (
                          <IconButton size="small" onClick={() => handleDeleteClick(c)} title="Remove margin" color="error">
                            <Delete />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingChampion?.margin_entitlement != null ? 'Edit margin' : 'Set margin entitlement'}</DialogTitle>
        <DialogContent>
          {editingChampion && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {editingChampion.email} {editingChampion.name && `(${editingChampion.name})`}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Margin entitlement (%)"
            type="number"
            value={marginInput}
            onChange={(e) => setMarginInput(e.target.value)}
            error={!!marginError}
            helperText={marginError}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveMargin} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Remove margin?</DialogTitle>
        <DialogContent>
          {championToDelete && (
            <Typography>
              Remove margin entitlement for {championToDelete.email}? They will no longer have a set margin %.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Remove</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChampionsManagement;
